import { UIEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  useLazyGetCommentLikersQuery,
  useLazyGetDiscussionLikersQuery,
} from "@/redux/service";
import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import UserAvatar from "@/domains/users/components/UserAvatar";
import { LikedByModalProps } from "@/domains/community/types";
import { UserWithoutOrganizationResponse } from "@/redux/users/types";

import "../styles.css";

const SCROLL_THRESHOLD = 50;

const LikedByModal = ({ entity, id, open, onClose }: LikedByModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [getDiscussionLikers] = useLazyGetDiscussionLikersQuery();
  const [getCommentLikers] = useLazyGetCommentLikersQuery();

  const [users, setUsers] = useState<UserWithoutOrganizationResponse[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const fetchRef = useRef(0);

  const fetchPage = useCallback(
    async (nextPage: number) => {
      const fetchId = ++fetchRef.current;
      setIsLoading(true);
      try {
        const trigger =
          entity === "discussion" ? getDiscussionLikers : getCommentLikers;
        const response = await trigger({ id, page: nextPage }).unwrap();
        if (fetchId !== fetchRef.current) return;

        setUsers((prev) =>
          nextPage === 1 ? response.results : [...prev, ...response.results],
        );
        setHasMore(Boolean(response.next));
        setPage(nextPage);
      } catch {
        if (fetchId === fetchRef.current) setHasMore(false);
      } finally {
        if (fetchId === fetchRef.current) setIsLoading(false);
      }
    },
    [entity, id, getDiscussionLikers, getCommentLikers],
  );

  useEffect(() => {
    if (!open) return;
    setUsers([]);
    setPage(1);
    setHasMore(true);
    fetchPage(1);
  }, [open, entity, id, fetchPage]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (
      !isLoading &&
      hasMore &&
      scrollHeight - scrollTop <= clientHeight + SCROLL_THRESHOLD
    ) {
      fetchPage(page + 1);
    }
  };

  const handleUserClick = (userId: number) => {
    onClose();
    navigate(`/users/${userId}`);
  };

  return (
    <BaseModal
      title={t("community.labels.likedBy")}
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
    >
      {users.length === 0 && !isLoading ? (
        <div className="liked-by-modal__empty">
          {t("community.labels.noLikesYet")}
        </div>
      ) : (
        <div
          className="liked-by-modal__list flex flex-col gap-100 scrollbar-neutral"
          onScroll={handleScroll}
        >
          {users.map((user) => (
            <div
              key={user.id}
              className="liked-by-modal__row flex items-center gap-100"
              onClick={() => handleUserClick(user.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  handleUserClick(user.id);
              }}
            >
              <UserAvatar user={user} size="large" />
              <span className="liked-by-modal__row-name body-s-medium">
                {user.fullName}
              </span>
            </div>
          ))}
          {isLoading && (
            <div className="liked-by-modal__loader flex items-center justify-center">
              <BaseSpinner size="small" isFullWidth={false} />
            </div>
          )}
        </div>
      )}
    </BaseModal>
  );
};

export default LikedByModal;
